// src/web/contactShadow.js
//
// A soft CONTACT SHADOW that makes the glasses look attached to the face rather
// than floating. Diagnostics proved the frame is placed correctly in 3D; the
// "floating" is perceptual — real glasses cast a soft shadow on the nose/brow
// that visually grounds them, and a flat webcam image has no depth cue to supply
// that. This adds it back.
//
// Implementation: a transparent quad with a procedurally-drawn soft elliptical
// dark gradient, posed in the SAME canonical space as the glasses (via the same
// pinned matrix), sitting just behind the lens plane on the skin and offset
// slightly downward so the shadow falls below the frame. It renders between the
// occluder (renderOrder 0) and the glasses (10), depth-tested so the face/
// occluder correctly hides it, and kept subtle so it reads as a shadow, not a
// smudge.

import {
  Mesh, PlaneGeometry, MeshBasicMaterial, CanvasTexture, Matrix4, Vector3, DoubleSide
} from 'three';

// Soft dark elliptical gradient texture (transparent PNG-like, drawn on canvas).
function makeShadowTexture() {
  const s = 256;
  const c = document.createElement('canvas');
  c.width = c.height = s;
  const ctx = c.getContext('2d');
  ctx.clearRect(0, 0, s, s);
  // Elliptical radial gradient: dark, soft, fading to transparent at the edges.
  // Squash vertically so the shadow is a horizontal smudge across the bridge.
  ctx.save();
  ctx.translate(s / 2, s * 0.46);
  ctx.scale(1.0, 0.52);                       // wide, short ellipse
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, s * 0.5);
  g.addColorStop(0.0, 'rgba(0,0,0,0.55)');
  g.addColorStop(0.45, 'rgba(0,0,0,0.28)');
  g.addColorStop(0.75, 'rgba(0,0,0,0.08)');
  g.addColorStop(1.0, 'rgba(0,0,0,0.0)');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(0, 0, s * 0.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  return new CanvasTexture(c);
}

export class ContactShadow {
  /**
   * @param {object} [opts]
   * @param {number} [opts.width]    quad width in canonical cm (≈ frame width)
   * @param {number} [opts.height]   quad height in canonical cm
   * @param {number} [opts.opacity]  overall strength (0..1)
   */
  constructor(opts = {}) {
    const qs = new URLSearchParams(window.location.search);
    const num = (k, fb) => { const v = parseFloat(qs.get(k)); return Number.isFinite(v) ? v : fb; };

    this._width  = opts.width  ?? num('shadoww', 13.5);
    this._height = opts.height ?? num('shadowh', 4.5);
    // Local offset (canonical cm) of the shadow from the bridge: a little DOWN
    // (-Y) so it falls below the frame, and slightly BACK toward the face (-Z in
    // canonical = toward the head) so it sits on the skin, behind the lens.
    this._offY = num('shadowy', -1.2);
    this._offZ = num('shadowz', 0.4);   // slightly toward camera (in front of skin)
    this._opacity = opts.opacity ?? num('shadowop', 0.5);

    this._tex = makeShadowTexture();
    this.material = new MeshBasicMaterial({
      map: this._tex,
      transparent: true,
      opacity: this._opacity,
      depthWrite: false,
      // depthTest FALSE so the depth-writing occluder (rendered first) doesn't
      // hide the shadow. The frame draws after (higher renderOrder) and over it.
      depthTest: false,
      side: DoubleSide,
    });

    this.mesh = new Mesh(new PlaneGeometry(this._width, this._height), this.material);
    this.mesh.renderOrder = 5;          // between occluder (0) and glasses (10)
    this.mesh.frustumCulled = false;
    this.mesh.matrixAutoUpdate = false;
    this.mesh.visible = false;

    // Local transform: the canonical bridge point + offset, facing +Z (camera).
    // The plane is built in XY facing +Z by default, which is what we want.
    this._local = new Matrix4().makeTranslation(0, 3.271 + this._offY, 5.236 + this._offZ);
    this._world = new Matrix4();

    this.enabled = true;
  }

  /**
   * Pose the shadow with the SAME pinned matrix as the glasses so it tracks the
   * face and sits on the bridge. The local offset places it on the skin below
   * the frame.
   * @param {THREE.Matrix4} pinnedM
   */
  update(pinnedM) {
    if (!this.enabled) { this.mesh.visible = false; return; }
    this._world.multiplyMatrices(pinnedM, this._local);
    this.mesh.matrix.copy(this._world);
    this.mesh.visible = true;
  }

  setEnabled(on) { this.enabled = on; if (!on) this.mesh.visible = false; }
  setOpacity(v) { this._opacity = v; this.material.opacity = v; }
  getOpacity() { return this._opacity; }
  hide() { this.mesh.visible = false; }
  dispose() { this._tex.dispose(); this.material.dispose(); this.mesh.geometry.dispose(); }
}
